import React, { useState, useEffect } from 'react';
import { commentApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './CommentManagement.css';

const CommentManagement = () => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [pages, setPages] = useState([]);

  // Check if user is master admin
  const isMasterAdmin = user?.role === 'master';

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadComments(selectedPage);
    }
  }, [selectedPage]);

  const loadPages = async () => {
    try {
      const response = await fetch('/api/pages');
      const pagesData = await response.json();
      setPages(pagesData);
      if (pagesData.length > 0) {
        setSelectedPage(pagesData[0].id);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      setError('Error loading pages');
    }
  };

  const loadComments = async (pageId) => {
    try {
      setLoading(true);
      setError('');
      const response = await commentApi.getByPageAdmin(pageId);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
      setError('Error loading comments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (commentId, isApproved) => {
    try {
      setError('');
      setSuccess('');
      await commentApi.approve(commentId, isApproved);
      setSuccess(`Comment ${isApproved ? 'approved' : 'disapproved'} successfully`);
      loadComments(selectedPage); // Reload comments
    } catch (error) {
      console.error('Error updating comment:', error);
      setError(error.response?.data?.message || 'Error updating comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await commentApi.delete(commentId);
      setSuccess('Comment deleted successfully');
      loadComments(selectedPage); // Reload comments
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError(error.response?.data?.message || 'Error deleting comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPageTitle = (pageId) => {
    const page = pages.find(p => p.id === parseInt(pageId));
    return page ? page.title : 'Unknown Page';
  };

  return (
    <div className="comment-management">
      <div className="comment-management-header">
        <h2>Comment Management</h2>
        <div className="page-selector">
          <label htmlFor="page-select">Select Page:</label>
          <select
            id="page-select"
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
          >
            {pages.map(page => (
              <option key={page.id} value={page.id}>
                {page.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isMasterAdmin && (
        <div className="info-message">
          <strong>Note:</strong> Only Master Admins can approve or disapprove comments. 
          You can view and delete comments.
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
          <button className="success-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="comments-container">
        {loading ? (
          <div className="loading">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="no-comments">No comments found for this page.</div>
        ) : (
          <div className="comments-list">
            {comments.map(comment => (
              <div key={comment.id} className={`comment-item ${!comment.isApproved ? 'unapproved' : ''}`}>
                <div className="comment-header">
                  <div className="comment-info">
                    <span className="comment-author">{comment.name}</span>
                    <span className="comment-email">({comment.email})</span>
                    <span className="comment-date">{formatDate(comment.createdAt)}</span>
                    <span className={`comment-status ${comment.isApproved ? 'approved' : 'pending'}`}>
                      {comment.isApproved ? 'Approved' : 'Pending Approval'}
                    </span>
                  </div>
                </div>
                
                <div className="comment-content">{comment.content}</div>
                
                <div className="comment-actions">
                  {isMasterAdmin && (
                    <>
                      {!comment.isApproved ? (
                        <button
                          className="approve-button"
                          onClick={() => handleApprove(comment.id, true)}
                        >
                          Approve
                        </button>
                      ) : (
                        <button
                          className="disapprove-button"
                          onClick={() => handleApprove(comment.id, false)}
                        >
                          Disapprove
                        </button>
                      )}
                    </>
                  )}
                  
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(comment.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentManagement; 